import { useState, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { AlertTriangle, X, ExternalLink, CheckCircle } from 'lucide-react';
import { ApplianceRecall } from '@/types';

interface RecallModalProps {
  recall: ApplianceRecall;
  applianceName: string;
  onClose: () => void;
}

const RecallModal = ({ recall, applianceName, onClose }: RecallModalProps) => {
  const [isAcknowledged, setIsAcknowledged] = useState(false);
  const [alreadyAcknowledged, setAlreadyAcknowledged] = useState(false);

  // Check localStorage for prior acknowledgment
  useEffect(() => {
    const key = `recall_ack_${recall.recallId}`;
    const stored = localStorage.getItem(key);
    if (stored) {
      setAlreadyAcknowledged(true);
      setIsAcknowledged(true);
    }
  }, [recall.recallId]);

  const handleAcknowledge = () => {
    // Mark as acknowledged in localStorage
    const key = `recall_ack_${recall.recallId}`;
    localStorage.setItem(key, 'true');
    setIsAcknowledged(true);
    setAlreadyAcknowledged(true);
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Don't show modal if already acknowledged, but still allow manual override
  // by checking local state separately, but here we honor the requirement:
  // - "Acknowledged" button closes modal (no Firestore write — localStorage key `recall_ack_{recall.recallId}`)
  
  return (
    <div 
      className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-white dark:bg-slate-900 w-full max-w-lg rounded-t-3xl sm:rounded-2xl p-6 m-0 sm:m-4 animate-in fade-in slide-in-from-bottom-10 duration-200 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center space-x-3">
            <div className="p-3 rounded-full bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400">
              <AlertTriangle className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Safety Recall</h2>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {applianceName}
              </p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-red-500"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="space-y-5">
          <div>
            <h3 className="text-sm font-medium text-slate-900 dark:text-white mb-2">
              Description
            </h3>
            <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
              {recall.description}
            </p>
          </div>

          <div className="p-4 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-900/30">
            <div className="flex items-start">
              <div className="flex-shrink-0 mt-0.5">
                <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
              </div>
              <div className="ml-3">
                <h4 className="text-sm font-semibold text-red-900 dark:text-red-100 mb-1">
                  Hazard
                </h4>
                <p className="text-sm text-red-800 dark:text-red-200">
                  {recall.hazard}
                </p>
              </div>
            </div>
          </div>

          <div className="p-4 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-900/30">
            <div className="flex items-start">
              <div className="flex-shrink-0 mt-0.5">
                <CheckCircle className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div className="ml-3">
                <h4 className="text-sm font-semibold text-emerald-900 dark:text-emerald-100 mb-1">
                  Remedy
                </h4>
                <p className="text-sm text-emerald-800 dark:text-emerald-200">
                  {recall.remedy}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 pt-4 border-t border-slate-100 dark:border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-3">
          {recall.url && (
            <a
              href={recall.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center px-3 py-2 text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              View CPSC Details →
            </a>
          )}

          <div className="flex items-center gap-3 w-full sm:w-auto">
            {alreadyAcknowledged ? (
              <div className="px-4 py-2 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300 text-sm font-medium flex items-center">
                <CheckCircle className="w-4 h-4 mr-2" />
                Acknowledged
              </div>
            ) : (
              <button
                onClick={handleAcknowledge}
                className="flex-1 sm:flex-none px-4 py-2 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors font-medium text-sm"
              >
                Acknowledge
              </button>
            )}
            
            <button
              onClick={onClose}
              className={`flex-1 sm:flex-none px-4 py-2 rounded-lg ${
                alreadyAcknowledged 
                  ? 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-lg shadow-indigo-500/30' 
                  : 'bg-slate-900 dark:bg-slate-700 text-white hover:bg-slate-800 dark:hover:bg-slate-600'
              } transition-colors font-medium text-sm`}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecallModal;
